import {useQuery, type UseQueryResult} from '@github-ui/react-query'
import {useParams} from '@github-ui/react-router'
import type {AgentTaskAppPayload} from '../types/agent-task-app-payload'
import {maybeNormalizeSession} from '../types/api-task'
/* eslint-disable-next-line @github-ui/github-monorepo/no-use-app-payload --
   useAppPayload is deprecated. See
   https://thehub.github.com/epd/engineering/dev-practicals/frontend/react/data-router/migration/app-payload-deprecation/ */
import {useAppPayload} from '@github-ui/react-core/use-app-payload'
import {useInitializeAuthToken} from './use-initialize-auth-token'

// Fetches a task payload by ID and normalizes its sessions. The immersive task
// view is always repo-less (mounted at /copilot/tasks/:taskID), so the task ID
// alone resolves the payload via the user-scoped endpoint.
async function fetchTaskPayload(taskID: string): Promise<AgentTaskAppPayload> {
  const url = new URL(`/copilot/tasks/${taskID}`, window.location.origin)
  const response = await fetch(url.toString(), {
    headers: {
      Accept: 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
    },
  })

  if (!response.ok) {
    throw new Error(`Failed to fetch task payload for ${taskID}`)
  }

  const data = await response.json()
  if (data.sessions) {
    data.sessions = data.sessions.map(maybeNormalizeSession)
  }
  return data
}

function hasExpectedKeys<T extends Record<string, unknown>>(data: unknown, keys: Array<keyof T>): data is T {
  return typeof data === 'object' && data !== null && keys.every(key => key in data)
}

const agentTaskKeys: Array<keyof AgentTaskAppPayload> = [
  'pull',
  'sessions',
  'currentUser',
  'currentUserCanPush',
  'steeringEnabled',
  'logsPollingInterval',
  'sessionsPollingInterval',
]

function isTaskAppPayload(payload: unknown): payload is AgentTaskAppPayload {
  return !!payload && hasExpectedKeys<AgentTaskAppPayload>(payload, agentTaskKeys)
}

type UrlParams = {
  taskID?: string
}

export function useOrFetchTaskPayload(): UseQueryResult<AgentTaskAppPayload> {
  const ssrPayload = useAppPayload<AgentTaskAppPayload>()
  const {taskID} = useParams<UrlParams>()
  const payloadMatchesUrl = !taskID || ssrPayload?.taskId === taskID
  const payloadIsValid = isTaskAppPayload(ssrPayload) && (ssrPayload.pull || ssrPayload.taskId) && payloadMatchesUrl
  const validPayload = payloadIsValid
    ? {...ssrPayload, sessions: ssrPayload.sessions.map(maybeNormalizeSession)}
    : undefined

  // Initialize auth token from SSR payload to avoid double token fetching
  useInitializeAuthToken(ssrPayload?.initialAuthToken)

  const queryResponse = useQuery<AgentTaskAppPayload>({
    queryKey: ['task-payload', taskID],
    queryFn: () => {
      if (!taskID) {
        throw new Error('Task ID is required to fetch a task payload')
      }
      return fetchTaskPayload(taskID)
    },
    initialData: validPayload,
    enabled: !!taskID && !validPayload,
    staleTime: Infinity,
  })

  return queryResponse
}
